import { TweenLite } from 'gsap';
import {
  spine,
  Sprite,
  Container,
  Graphics,
  Texture,
  particles,
  RenderTexture,
  Application,
} from 'pixi.js';

import { MultiColorReplaceFilter } from '@pixi/filter-multi-color-replace';

import animationTexturesMapping from './data/animationTexturesMapping.json';
import positionCrutches from './data/positionCrutches.json';
import baseEmitterOptions from './data/baseEmitterOptions.json';
import whiteEmitterOptions from './data/whiteEmitterOptions.json';

import {
  getDragonSpriteByRace,
  addSprite,
  loadDragonsPatterns,
} from './dragonRenderer';

import {
  ANIMATIONS,
  DRAGONS_BASE_URL,
  PARTICLES,
  SIZE_TYPES,
} from './constants';

import {
  getDragonColor,
  getLightenColor,
  hexToNumber,
} from './colorUtils';

import {
  mergeTwoTextures,
  renderSprite,
} from './common';

const recolorShader = `
varying vec2 vTextureCoord;
uniform sampler2D uSampler;

uniform vec3 originalColors[1];
uniform vec3 targetColors[1];

uniform float epsilon;

void main(void)
{
    gl_FragColor = texture2D(uSampler, vTextureCoord);

    float alpha = gl_FragColor.a;
    if (alpha < 0.0001)
    {
      return;
    }

    vec3 color = gl_FragColor.rgb / alpha;

    vec3 origColor = originalColors[0];

    vec3 colorDiff = origColor - color;

    float red = color.r;
    float green = color.g;
    float blue = color.b;

    float k = red/ epsilon;

    float targetRed =  targetColors[0].r*k;
    float targetGreen = targetColors[0].g*k;
    float targetBlue = targetColors[0].b*k;

    gl_FragColor = vec4(targetRed * alpha, targetGreen * alpha, targetBlue * alpha, alpha);
}
`;

const applicationsCache = {};

const defaultEmitterPosition = {
  offsetX: -50,
  offsetY: 0,
  minAngle: 0,
  maxAngle: 15,
};

export const animationTree = {
  idle: [
    [ANIMATIONS.IDLE_TO_SLEEP, ANIMATIONS.SLEEP],
    [ANIMATIONS.IDLE_TO_FLY, ANIMATIONS.FLY],
  ],
  sleep: [[ANIMATIONS.SLEEP_TO_IDLE, ANIMATIONS.IDLE]],
  fly: [
    [ANIMATIONS.FLY_TO_IDLE, ANIMATIONS.IDLE],
    [ANIMATIONS.ATTACK_FAR, ANIMATIONS.FLY],
  ],
};

function getAttackMask(app, dragon, size) {
  const maskContainer = new Container();
  const fireMaskGraph = new Graphics();
  const finalMaskTexture = RenderTexture.create(1680, 1500);
  const fireMask = getDragonSpriteByRace(
    dragon.head.race,
    dragon.head.type,
    'attack_mask',
    size,
  );

  maskContainer.addChild(fireMask);
  fireMaskGraph.beginFill(0xFFFFFF);
  fireMaskGraph.drawRect(0, 0, 1000, 1300);
  fireMaskGraph.endFill();
  maskContainer.addChild(fireMaskGraph);

  // TODO: remove this shit. Need for correct attack mask for medium size dragon animation
  if (size === SIZE_TYPES.SMALL_2X) {
    fireMaskGraph.x = 480;
  } else {
    fireMaskGraph.x = 680;
  }

  app.renderer.render(maskContainer, finalMaskTexture);

  return new Sprite(finalMaskTexture);
}

function initParticles(color, mask, app) {
  const particleContainerParent = new Container();
  const baseParticleContainer = new Container();
  const whiteParticleContainer = new Container();

  const baseEmitter = new particles.Emitter(
    baseParticleContainer,
    PARTICLES.map(particle => Texture.from(`${DRAGONS_BASE_URL}/${particle}`)),
    {
      ...baseEmitterOptions,
      color: {
        start: getLightenColor(color, 0.4),
        end: color,
      },
    },
  );

  const whiteEmitter = new particles.Emitter(
    whiteParticleContainer,
    PARTICLES.map(particle => Texture.from(`${DRAGONS_BASE_URL}/${particle}`)),
    whiteEmitterOptions,
  );

  particleContainerParent.addChild(baseParticleContainer);
  particleContainerParent.addChild(whiteParticleContainer);
  particleContainerParent.mask = mask;
  app.stage.addChildAt(particleContainerParent, 1);

  baseEmitter.emit = false;

  return [
    baseEmitter,
    whiteEmitter,
  ];
}

function runNextAnimation(animationStack, currentAnimation, sprite) {
  let animation = currentAnimation;

  if (animationStack.length === 0) {
    sprite.state.setAnimation(0, animation, false);
  } else {
    const nextAnimation = animationStack.shift();

    if (sprite.state.hasAnimation(nextAnimation)) {
      sprite.state.setAnimation(0, nextAnimation, false);
      animation = nextAnimation;
    } else {
      return runNextAnimation(animationStack, sprite);
    }
  }

  if (animation !== ANIMATIONS.IDLE && animation !== ANIMATIONS.FLY) {
    sprite.state.setEmptyAnimation(1, 1);
  }

  return animation;
}

function renderAnimation({
  id,
  genome,
  color,
  canvas,
  width,
  height,
  skeleton,
  size,
}) {
  if (applicationsCache[id]) {
    destroyAnimationCanvas(id);
  }

  const app = new Application(width, height, { transparent: true, view: canvas });
  const colorFilter = new MultiColorReplaceFilter([[0x7F7F7F, hexToNumber(color)]], 0.5);

  colorFilter.fragmentSrc = recolorShader;

  const finalTextures = animationTexturesMapping.reduce((acc, {
    base,
    part,
    item,
    postfix,
    noPattern,
    samePattern,
    empty = [],
  }) => ({
    ...acc,
    [base]: renderSprite(addSprite(
      size,
      colorFilter,
      genome[part].race,
      genome.pattern.race,
      genome[part].type,
      genome.pattern.type,
      new Container(),
      `${item}_base${postfix}`,
      `${item}_color${postfix}`,
      noPattern ? null : `pattern_base_${item + (samePattern ? '' : postfix)}`,
    ), app.renderer, size),
    ...empty.reduce((emptyAcc, key) => ({
      ...emptyAcc,
      [key]: Texture.EMPTY,
    }), {}),
  }), {});

  const mask = new Sprite(Texture.from(`${DRAGONS_BASE_URL}/${size}/wing_mask`));
  const wingPartUp = new Sprite(finalTextures['wings/wing_right_base_up']);
  const wingPartDown = new Sprite(finalTextures['wings/wing_right_base_down']);

  wingPartUp.mask = mask;
  wingPartDown.mask = mask;

  finalTextures['wings/wing_right_base_up_mask'] = renderSprite(wingPartUp, app.renderer, size);
  finalTextures['wings/wing_right_base_down_mask'] = renderSprite(wingPartDown, app.renderer, size);

  finalTextures['tail/tail_base'] = mergeTwoTextures(
    finalTextures['tail/tail_base'],
    finalTextures['tail/spike_tail_base'],
    app.renderer,
    size,
  );
  finalTextures['tail/spike_tail_base'] = Texture.EMPTY;

  return buildSpine(finalTextures, app, skeleton, id);
}

function buildSpine(textures, app, skeleton, id) {
  const atlas = new spine.core.TextureAtlas();

  atlas.addTextureHash(textures, true);

  const spineAtlasLoader = new spine.core.AtlasAttachmentLoader(atlas);
  const spineJsonParser = new spine.core.SkeletonJson(spineAtlasLoader);
  const spineData = spineJsonParser.readSkeletonData(skeleton);
  const sprite = new spine.Spine(spineData);

  const cache = {
    app,
    sprite,
  };

  applicationsCache[id] = cache;

  return cache;
}

async function renderIdleAnimation(options) {
  const {
    genome: dragon,
    size,
    color,
  } = options;

  const {
    app,
    sprite: dragonSprite,
  } = renderAnimation(options);

  const crutch = positionCrutches.find(({ type, race }) =>
    race === dragon.head.race && type === dragon.head.type);
  const emitterPosition = crutch ? crutch.position : defaultEmitterPosition;
  const animations = Object.keys(animationTree);

  let animationStack = [];
  let currentAnimation = animations[Math.floor(animations.length * Math.random())];
  let needToEmit = false;
  let lastEvent = Date.now();

  dragonSprite.autoUpdate = false;
  app.stage.addChild(dragonSprite);
  dragonSprite.x = 250;
  dragonSprite.scale.set(0.75, 0.75);

  if (currentAnimation === ANIMATIONS.FLY) {
    dragonSprite.y = 300;
  } else {
    dragonSprite.y = 400;
  }

  currentAnimation = runNextAnimation(animationStack, currentAnimation, dragonSprite);

  dragonSprite.state.addListener({
    complete: (entry) => {
      if (entry.trackIndex === 0) {
        currentAnimation = runNextAnimation(animationStack, currentAnimation, dragonSprite);
      }
    },
    event: (entry, event) => {
      if (event.stringValue === 'start') {
        needToEmit = true;
      } else if (event.stringValue === 'stop') {
        needToEmit = false;
      } else if (event.stringValue === 'up') {
        TweenLite.to(dragonSprite, 0.5, { y: 300 });
      } else if (event.stringValue === 'down') {
        TweenLite.to(dragonSprite, 0.5, { y: 400 });
      }
    },
  });

  const mask = getAttackMask(app, dragon, size);
  const emitters = initParticles(color, mask, app);

  mask.anchor.x = 340 / 1680;
  mask.anchor.y = 400 / 1500;
  app.stage.addChild(mask);
  mask.scale.set(0.75, 0.75);
  mask.alpha = 1;

  const eventsDelta = 1000;

  const headSlot = dragonSprite.skeleton.findSlot('head/head_base_closed');
  const firePoint = dragonSprite.skeleton.findBone('firePoint');

  app.ticker.add((time) => {
    const now = Date.now();

    if (now > lastEvent + eventsDelta) {
      if (Math.random() < 0.0045) {
        const sequences = animationTree[currentAnimation];

        if (sequences && sequences.length !== 0) {
          const sequence = sequences[Math.floor(sequences.length * Math.random())];

          if (sequence) {
            animationStack = sequence.concat();
            lastEvent = now;
          }
        }
      }
    }

    dragonSprite.update(time * 0.02);
    mask.x = headSlot.currentSprite.getGlobalPosition().x;
    mask.y = headSlot.currentSprite.getGlobalPosition().y;
    mask.rotation = firePoint.localToWorldRotation(13.32) * Math.PI / 180;

    emitters.forEach((em) => {
      const emitter = em;

      if (needToEmit) {
        const emitterX = firePoint.worldX + dragonSprite.x + emitterPosition.offsetX;
        const emitterY = firePoint.worldY + dragonSprite.y + emitterPosition.offsetY;
        const firePointRotation = firePoint.localToWorldRotation(0);

        emitter.resetPositionTracking();
        emitter.minStartRotation = emitterPosition.minAngle + firePointRotation;
        emitter.maxStartRotation = emitterPosition.maxAngle + firePointRotation;
        emitter.spawnPos.x = emitterX;
        emitter.spawnPos.y = emitterY;
        emitter.emit = true;
      } else {
        emitter.resetPositionTracking();
        emitter.spawnPos.x = 0;
        emitter.spawnPos.y = 0;
        emitter.emit = false;
      }

      emitter.update(time * 0.02);
    });
  });
}

export async function renderAnimationToCanvas(
  options,
  size = SIZE_TYPES.SMALL_2X,
) {
  await loadDragonsPatterns(options.genome, size);

  return renderIdleAnimation({
    ...options,
    size,
    color: getDragonColor(options.races),
  });
}

export function destroyAnimationCanvas(id) {
  const cache = applicationsCache[id];

  try {
    if (cache) {
      cache.sprite.destroy(true);
      cache.app.destroy(true);
      applicationsCache[id] = null;
    }
  } catch (error) {
    applicationsCache[id] = null;
  }
}
